// Store Configuration Types
export interface StoreConfig {
  id: string;
  name: string;
  slug: string;
  domain: string;
  logo: string;
  logoText: string;
  favicon: string;
  serverIp: string;
  serverStatus: 'online' | 'offline' | 'maintenance';
  playersOnline: number;
  playersRecord: number;
  registeredPlayers: number;
  uptime: string;
  theme: Theme;
  background: Background;
  heroTitle: string;
  heroSubtitle: string;
  heroButtonText: string;
  heroButtonAction: string;
  social: SocialLinks;
  navigation: NavigationItem[];
  footer: Footer;
  currency: Currency;
  language: string;
  features: Features;
}

export interface Theme {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  backgroundSecondary: string;
  foreground: string;
  card: string;
  border: string;
  radius: string;
}

export interface Background {
  type: 'image' | 'gradient' | 'color';
  image?: string | null;
  gradient?: string | null;
  overlay?: boolean;
  overlayOpacity?: number;
}

export interface SocialLinks {
  discord?: string;
  youtube?: string;
  instagram?: string;
  twitter?: string;
  tiktok?: string;
  facebook?: string;
}

export interface NavigationItem {
  id: string;
  label: string;
  href: string;
  icon: string;
}

export interface Footer {
  about: string;
  quickLinks: FooterLink[];
  legalLinks: FooterLink[];
}

export interface FooterLink {
  label: string;
  href: string;
}

export interface Currency {
  code: string;
  symbol: string;
  locale: string;
}

export interface Features {
  store: boolean;
  forum: boolean;
  wiki: boolean;
  tickets: boolean;
  blog: boolean;
  credits: boolean;
}

// Category Types
export interface Category {
  id: string;
  name: string;
  slug: string;
  description: string;
  image: string;
  productCount: number;
}

// Product Types
export interface Product {
  id: string;
  categoryId: string;
  categorySlug: string; // Slug da categoria para construir URLs
  name: string;
  slug: string;
  shortDescription?: string; // Short description for cards
  description: string;
  price: number;
  originalPrice: number | null;
  image: string;
  benefits: string[];
  duration: string;
  durationDays?: number | null; // Optional: Duration in days from API (null/undefined = permanent)
  minQuantity?: number; // Minimum quantity per purchase
  purchaseLimitPerUser?: number; // Purchase limit per user
  featured: boolean;
  serverId?: string | null; // Servidor de entrega (monta o link do produto fora da loja)
  /** COMMAND (no jogo) ou FILE, LICENSE_KEY (produto digital). */
  deliveryKind?: 'COMMAND' | 'FILE' | 'LICENSE_KEY';
  /** FILE: downloads por compra (null = sem limite). */
  downloadLimit?: number | null;
  /** Chave de licença sem estoque: não dá para comprar. */
  soldOut?: boolean;
}

// Blog Types
export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  image: string;
  category: string;
  author: Author;
  date: string;
  views: number;
}

export interface Author {
  name: string;
  avatar: string | null;
  role: string;
}

// User Types
export interface User {
  id: string;
  username: string;
  email: string;
  avatar: string | null;
  role: string;
  credits: number;
  createdAt: string;
  isOnline: boolean;
}

// Widget Types
export interface TopBuyer {
  id: string;
  username: string;
  avatar: string | null;
  totalSpent: number;
  purchases: number;
  rank: number;
}

export interface GoalReward {
  threshold: number;
  description: string;
  unlocked: boolean;
}

export interface StoreGoal {
  title: string;
  description: string;
  currentAmount: number;
  targetAmount: number;
  rewards: GoalReward[];
  endsAt: string;
}

export interface DiscordWidget {
  serverId: string;
  inviteLink: string;
  serverName: string;
  membersOnline: number;
  membersTotal: number;
}

// Cart Types
export type CartItemType = 'PRODUCT' | 'CREDIT' | 'VIP_UPGRADE';

/** Upgrade de VIP no carrinho: o preço vem do servidor a cada leitura. */
export interface CartVipUpgrade {
  fromPlayerVipId: string;
  toVipDefinitionId: string;
  permanent: boolean;
  /** Ainda vale (o VIP atual continua valendo e o novo está à venda). */
  available: boolean;
  /** Por que não vale mais, já traduzido. */
  reason?: string;
}

export interface CartItem {
  productId: string | null;
  quantity: number;
  discountPercent?: number;
  priceWithDiscount?: number;
  product?: Product;
  type?: CartItemType;
  customAmount?: number | null;
  /** Nome do item quando não é produto (upgrade de VIP). */
  productName?: string;
  vipUpgrade?: CartVipUpgrade;
}

export interface Coupon {
  code: string;
  type: 'percentage' | 'fixed';
  value: number;
}

// Wiki Types
export interface WikiArticle {
  id: string;
  title: string;
  content: string;
}

export interface WikiCategory {
  id: string;
  name: string;
  icon: string;
  articles: WikiArticle[];
}

// Support Types
export interface TicketMessage {
  id: string;
  author: string;
  content: string;
  date: string;
}

export interface SupportTicket {
  id: string;
  title: string;
  category: string;
  status: 'open' | 'closed' | 'in_progress' | 'resolved';
  lastUpdate: string;
  messages: TicketMessage[];
}

export interface TicketCategory {
  id: string;
  label: string;
}

// Order/Transaction Types
export interface OrderItem {
  productId: string;
  quantity: number;
}

export interface Order {
  id: string;
  items: OrderItem[];
  total: number;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  date: string;
  paymentMethod: string;
}

export interface Transaction {
  id: string;
  type: 'deposit' | 'withdraw';
  amount: number;
  description: string;
  date: string;
}

// Payment Types
export enum PaymentMethod {
  CREDIT_CARD = 'CREDIT_CARD',
  DEBIT_CARD = 'DEBIT_CARD',
  PIX = 'PIX',
  BOLETO = 'BOLETO',
  PAYPAL = 'PAYPAL',
  WALLET = 'WALLET',
  MERCADO_PAGO = 'MERCADO_PAGO', // Checkout Pro
}

// Purchase Types
export interface Purchase {
  id: string;
  userId: string;
  productId: string;
  product: Product;
  amount: number;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  createdAt: string;
}

// Order Types
export interface Order extends Purchase {}

// Ticket Types
export interface TicketCategory {
  id: string;
  name: string;
  description: string;
}
